// External product-import formats shared by the Wipe and Manual Products
// admin tables (plan: feat-manual-products.md, 2026-10-15). Both pages
// export the same PPD CSV and Jobalots XLSX; the per-row mapping lives
// with each page (WipeAdmin.ppdValue / ManualProductsAdmin.manualPpdValue),
// the headers, constants and download helpers live here so the external
// column order has ONE source of truth.
import ExcelJS from "exceljs";

// PPD export CSV columns. This is an EXTERNAL system's fixed import format —
// every header must match it verbatim (including "Product Catagory" and the
// "(keep but do not adjust)" suffixes), so none of these go through i18n.
// The trailing "v160725" column is the format's version marker.
export const PPD_HEADER = [
    "EAN",
    "EAN_channel(MasterKey)",
    "EAN_owner",
    "EAN_EAN",
    "FBA_EAN",
    "EAN_Color",
    "Shopify ID",
    "Brightpearl ID",
    "Product Title",
    "Product Description",
    "Product Catagory",
    "Product Supplier",
    "Customer",
    "Product Type",
    "Product Manufacturer",
    "Production (1=Yes 0=NO)",
    "Food (1=Yes 0=NO)",
    "Perishable (1=Yes 0=NO)",
    "Batch (1=Yes 0=NO)",
    "Hazardous (1=Yes 0=NO)",
    "Aerosol (1=Yes 0=NO)",
    "Battery (1=Yes 0=NO)",
    "Liquid (1=Yes 0=NO)",
    "Serial Scan (1=Yes 0=NO)",
    "Minimum Holding Qty",
    "Minimum Stock Level",
    "Reorder Qty",
    "Product Height (mm)",
    "Product Width (mm)",
    "Product Depth (mm)",
    "Product Weight (grams)",
    "Bookon Cost",
    "Product Cost",
    "Product RRP",
    "Product Sell Price",
    "Purchase Inc Vat 1=Inc/0=Ex",
    "Purchase Vat Value",
    "Sale Inc Vat 1=Inc/0=Ex",
    "Sales Vat Value",
    "Commodity Code In",
    "Commodity Code Out",
    "Country Origin",
    "Commodity Text",
    "FBA Fulfilment Fee £",
    "Amazon Referral Fee %",
    "Ebay Referral Fee %",
    "Carton Qty",
    "Pack Qty",
    "Product Size",
    "Created (keep but do not adjust)",
    "Created By (keep but do not adjust)",
    "v160725",
] as const;

// PPD columns that carry the same value on every row. Values supplied by the
// operations spec (2026-07-08): the literal apostrophes are what the source
// sheet carries for the ID columns, and empty string means the column is
// present but always blank. Columns NOT listed here are per-product.
export const PPD_CONSTANTS: Record<string, string | number> = {
    "EAN_EAN": "Pink",
    "FBA_EAN": "'",
    "EAN_Color": "",
    "Shopify ID": "'",
    "Brightpearl ID": "'",
    "Product Catagory": "Pink",
    "Product Supplier": "Amazon LaaS",
    "Customer": "Jobalots UK",
    "Product Type": "Jobalots UK",
    "Product Manufacturer": "Amazon LaaS",
    "Production (1=Yes 0=NO)": 0,
    "Food (1=Yes 0=NO)": 0,
    "Perishable (1=Yes 0=NO)": 0,
    "Batch (1=Yes 0=NO)": 0,
    "Hazardous (1=Yes 0=NO)": 0,
    "Aerosol (1=Yes 0=NO)": 0,
    "Battery (1=Yes 0=NO)": 0,
    "Liquid (1=Yes 0=NO)": 0,
    "Serial Scan (1=Yes 0=NO)": 0,
    "Minimum Holding Qty": 0,
    "Minimum Stock Level": 0,
    "Reorder Qty": 0,
    "Product Height (mm)": 0,
    "Product Width (mm)": 0,
    "Product Depth (mm)": 0,
    "Purchase Inc Vat 1=Inc/0=Ex": 0,
    "Purchase Vat Value": 0,
    "Sale Inc Vat 1=Inc/0=Ex": 0,
    "Sales Vat Value": 0,
    "Commodity Code In": "",
    "Commodity Code Out": "",
    "Country Origin": "",
    "Commodity Text": "",
    "FBA Fulfilment Fee £": 0,
    "Amazon Referral Fee %": 0,
    "Ebay Referral Fee %": 0,
    "Carton Qty": 0,
    "Pack Qty": 0,
    "Product Size": "",
    "Created (keep but do not adjust)": "",
    "Created By (keep but do not adjust)": "",
    "v160725": "",
};

// The Jobalots import XLSX (header + constants) is built server-side since
// 2026-10-31 — BE app/warehouse/services/jobalotsExport.js — so its
// multi-language title column can be filled; the admin tables queue it.

// A number-ish export cell: "" for missing / non-numeric, else the number
// (optionally rounded to a whole unit, e.g. grams).
export const ppdNum = (v: number | string | null | undefined, round = false): string | number => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return round ? Math.round(n) : n;
};

// RFC-4180 style CSV: quote a field only when it contains a comma, quote,
// or newline; double any embedded quotes.
export const csvField = (v: string | number) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const downloadCsv = (filename: string, rows: (string | number)[][]) => {
    const text = rows.map(r => r.map(csvField).join(",")).join("\r\n");
    // UTF-8 BOM so Excel opens the file as UTF-8 (the £ in
    // "FBA Fulfilment Fee £" renders wrong without it).
    const BOM = String.fromCharCode(0xfeff);
    triggerDownload(new Blob([BOM + text], { type: "text/csv;charset=utf-8;" }), filename);
};

// One-sheet XLSX. `textColumns` (0-based indexes) are forced to Text so
// Excel keeps long barcode / id digit strings verbatim.
export const downloadXlsx = async (
    filename: string,
    header: string[],
    rows: (string | number)[][],
    textColumns?: Set<number>,
    sheetName = "Export",
) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(sheetName);
    ws.addRow(header);
    for (const r of rows) ws.addRow(r);
    textColumns?.forEach(i => { ws.getColumn(i + 1).numFmt = "@"; });
    const buffer = await wb.xlsx.writeBuffer();
    triggerDownload(new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }), filename);
};
